import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { NavigationListContext, useNavigationListContext } from '@/components/ui/navigation-list-context';
import { sidebarNavItem, sidebarNestedContainer, sidebarSectionHeading, sidebarSectionTitle } from '@/components/ui/sidebar/helpers/sidebar-theme';
import { SIDEBAR_NAV_ITEM_STATE } from './sidebar/types/sidebar-theme-enums';
import { cn } from '@/lib/utils';

import { NAVIGATION_LIST_ITEM_DEPTH, NAVIGATION_LIST_ITEM_STATE, NAVIGATION_LIST_LABEL_LEVEL, NAVIGATION_LIST_ROOT_AS } from './types/navigation-list-variants-enums';

export interface NavigationListRootProps extends Omit<
  React.ComponentProps<'nav'>,
  'as' | 'ref'
> {
  as?: NAVIGATION_LIST_ROOT_AS;
}

function NavigationListRoot({
  as = NAVIGATION_LIST_ROOT_AS.NAV,
  className,
  ...props
}: NavigationListRootProps) {
  const Comp = as;

  return (
    <NavigationListContext value={true}>
      <Comp
        {...props}
        // gap-3.5 (~14px / 0.5r) owns Label|Heading → Group when they are Root siblings (R14 G-HCHILD).
        // Collapsible wrappers must repeat the same flex+gap — Root gap cannot reach nested Label/Group.
        className={cn('flex flex-col gap-3.5', className)}
        data-slot="navigation-list-root"
      />
    </NavigationListContext>
  );
}

type NavigationListGroupProps = React.ComponentProps<'div'>;

function NavigationListGroup({ className, ...props }: NavigationListGroupProps) {
  useNavigationListContext('NavigationList.Group');

  return (
    <div
      {...props}
      role="group"
      className={cn(sidebarNestedContainer(), className)}
      data-slot="navigation-list-group"
    />
  );
}

export interface NavigationListLabelProps extends React.ComponentProps<'button'> {
  asChild?: boolean;
  level?: NAVIGATION_LIST_LABEL_LEVEL;
}

function NavigationListLabel({
  asChild = false,
  className,
  children,
  level = NAVIGATION_LIST_LABEL_LEVEL.SECTION,
  type = 'button',
  ...props
}: NavigationListLabelProps) {
  useNavigationListContext('NavigationList.Label');
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      type={asChild ? undefined : type}
      {...props}
      className={cn(
        sidebarSectionTitle({
          state: 'default',
        }),
        'w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2',
        className
      )}
      data-level={level}
      data-slot="navigation-list-label"
    >
      {children}
    </Comp>
  );
}

export interface NavigationListHeadingProps extends React.ComponentProps<'div'> {
  asChild?: boolean;
}

function NavigationListHeading({
  asChild = false,
  className,
  children,
  ...props
}: NavigationListHeadingProps) {
  useNavigationListContext('NavigationList.Heading');
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      {...props}
      className={cn(sidebarSectionHeading(), className)}
      data-slot="navigation-list-heading"
    >
      {children}
    </Comp>
  );
}

export interface NavigationListItemProps extends Omit<
  React.ComponentProps<'div'>,
  'children'
> {
  asChild?: boolean;
  children?: React.ReactNode;
  depth?: NAVIGATION_LIST_ITEM_DEPTH;
  disabled?: boolean;
  state?: NAVIGATION_LIST_ITEM_STATE;
}

function NavigationListItem({
  asChild = false,
  className,
  depth = NAVIGATION_LIST_ITEM_DEPTH.DEFAULT,
  disabled = false,
  state = NAVIGATION_LIST_ITEM_STATE.DEFAULT,
  children,
  tabIndex,
  ...props
}: NavigationListItemProps) {
  useNavigationListContext('NavigationList.Item');
  const Comp = asChild ? Slot : 'div';
  const resolvedState =
    disabled || state === NAVIGATION_LIST_ITEM_STATE.DEFAULT
      ? SIDEBAR_NAV_ITEM_STATE.DEFAULT
      : SIDEBAR_NAV_ITEM_STATE.ACTIVE;
  const stateVariant =
    resolvedState === SIDEBAR_NAV_ITEM_STATE.ACTIVE ? 'active' : 'default';

  return (
    <div
      data-depth={depth}
      data-slot="navigation-list-item-rail"
    >
      <Comp
        {...props}
        aria-disabled={disabled || undefined}
        className={cn(
          sidebarNavItem({
            state: stateVariant,
          }),
          disabled &&
            'cursor-not-allowed text-content-disabled hover:bg-transparent pointer-fine:hover:bg-transparent pointer-fine:hover:text-content-disabled',
          className
        )}
        data-disabled={disabled ? 'true' : undefined}
        data-slot="navigation-list-item"
        data-state={stateVariant}
        tabIndex={disabled ? -1 : tabIndex}
      >
        {children}
      </Comp>
    </div>
  );
}

export { NavigationListGroup, NavigationListHeading, NavigationListItem, NavigationListLabel, NavigationListRoot };
